import React, { useState, useEffect, useMemo } from 'react';
import { getDatabase, ref, get, remove, update } from 'firebase/database';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { app } from '../../firebase';
import { toast } from 'react-toastify';
import { FaSearch, FaTimes, FaChevronLeft, FaChevronRight, FaEdit, FaKey, FaEnvelope, FaCheckCircle, FaLock, FaCopy } from 'react-icons/fa';
import { getFunctions, httpsCallable } from 'firebase/functions';

const EXPIRING_SOON_DAYS = 14;

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// Derive package state from endDate rather than trusting subscription.status,
// which is only updated when the subscription checker runs.
const getPackageInfo = (employer) => {
  const sub = employer?.subscription;
  if (!sub) return { state: 'none', label: 'No package' };

  const name = sub.packageName || sub.package?.name || 'Unknown package';
  const end = sub.endDate ? new Date(sub.endDate) : null;
  if (!end || isNaN(end.getTime())) {
    return { state: 'none', label: sub.status === 'inactive' ? 'Inactive' : 'No package', name };
  }

  const daysLeft = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
  let state = 'active';
  if (daysLeft < 0) state = 'expired';
  else if (daysLeft <= EXPIRING_SOON_DAYS) state = 'expiring';

  const label = { active: 'Active', expiring: 'Expiring soon', expired: 'Expired' }[state];
  return { state, label, name, endDate: sub.endDate, daysLeft };
};

const PACKAGE_PILL = {
  active: 'bg-green-100 text-green-800',
  expiring: 'bg-amber-100 text-amber-800',
  expired: 'bg-red-100 text-red-800',
  none: 'bg-gray-100 text-gray-600',
};

const describeDaysLeft = (info) => {
  if (info.daysLeft === undefined) return '';
  if (info.daysLeft < 0) return `${Math.abs(info.daysLeft)} day${Math.abs(info.daysLeft) === 1 ? '' : 's'} ago`;
  if (info.daysLeft === 0) return 'today';
  return `${info.daysLeft} day${info.daysLeft === 1 ? '' : 's'} left`;
};

const AllEmployers = () => {
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  // Reset password state
  const [isResetting, setIsResetting] = useState(null);

  // Temporary password state
  const [isGeneratingPassword, setIsGeneratingPassword] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState(null);

  // Resend verification state
  const [isResendingVerification, setIsResendingVerification] = useState(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [packageFilter, setPackageFilter] = useState('all');
  const [sortBy, setSortBy] = useState('default');

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const db = getDatabase(app);
  const auth = getAuth(app);

  useEffect(() => {
    const fetchEmployers = async () => {
      try {
        const employersRef = ref(db, 'employers');
        const snapshot = await get(employersRef);

        if (snapshot.exists()) {
          const employersData = snapshot.val();
          const employersArray = Object.entries(employersData).map(([id, data]) => ({
            id,
            ...data,
            fullName: `${data.firstName} ${data.lastName}`,
          }));
          setEmployers(employersArray);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching employers:', error);
        toast.error('Error loading employers');
        setLoading(false);
      }
    };

    fetchEmployers();
  }, [db]);

  // Filter employers based on search term
  const packageCounts = useMemo(() => {
    const counts = { active: 0, expiring: 0, expired: 0, none: 0 };
    employers.forEach(e => { counts[getPackageInfo(e).state]++; });
    return counts;
  }, [employers]);

  const filteredEmployers = useMemo(() => {
    const search = searchTerm.toLowerCase();
    let list = employers.filter(employer => {
      if (packageFilter !== 'all' && getPackageInfo(employer).state !== packageFilter) return false;
      if (!search) return true;
      return (
        employer.fullName?.toLowerCase().includes(search) ||
        employer.email?.toLowerCase().includes(search) ||
        employer.phone?.toLowerCase().includes(search) ||
        employer.companyName?.toLowerCase().includes(search) ||
        employer.company?.toLowerCase().includes(search) ||
        getPackageInfo(employer).name?.toLowerCase().includes(search)
      );
    });

    if (sortBy === 'expiry') {
      // Soonest end date first; employers with no package go last
      const key = (e) => {
        const end = e.subscription?.endDate ? new Date(e.subscription.endDate).getTime() : NaN;
        return isNaN(end) ? Infinity : end;
      };
      list = [...list].sort((a, b) => key(a) - key(b));
    }
    return list;
  }, [employers, searchTerm, packageFilter, sortBy]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredEmployers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEmployers = filteredEmployers.slice(startIndex, endIndex);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, packageFilter, sortBy]);

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = new Set(paginatedEmployers.map(emp => emp.id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (employerId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(employerId)) {
      newSelected.delete(employerId);
    } else {
      newSelected.add(employerId);
    }
    setSelectedIds(newSelected);
  };

  const isAllSelected = paginatedEmployers.length > 0 &&
    paginatedEmployers.every(emp => selectedIds.has(emp.id));

  const isSomeSelected = paginatedEmployers.some(emp => selectedIds.has(emp.id));

  const handleDelete = async (employerId) => {
    if (window.confirm('Are you sure you want to delete this employer?')) {
      try {
        await remove(ref(db, `employers/${employerId}`));
        toast.success('Employer deleted successfully');
        setEmployers(employers.filter(e => e.id !== employerId));
        // Remove from selection if deleted
        const newSelected = new Set(selectedIds);
        newSelected.delete(employerId);
        setSelectedIds(newSelected);
      } catch (error) {
        console.error('Error deleting employer:', error);
        toast.error('Error deleting employer');
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedIds.size} selected employer(s)?`)) {
      try {
        const deletePromises = Array.from(selectedIds).map(id =>
          remove(ref(db, `employers/${id}`))
        );
        await Promise.all(deletePromises);
        toast.success(`${selectedIds.size} employer(s) deleted successfully`);
        setEmployers(employers.filter(e => !selectedIds.has(e.id)));
        setSelectedIds(new Set());
      } catch (error) {
        console.error('Error deleting employers:', error);
        toast.error('Error deleting employers');
      }
    }
  };

  // Reset password handler
  const handleResetPassword = async (employer) => {
    if (!employer.email) {
      toast.error('No email address found for this employer');
      return;
    }

    if (window.confirm(`Send password reset email to ${employer.email}?`)) {
      setIsResetting(employer.id);
      try {
        await sendPasswordResetEmail(auth, employer.email);
        toast.success(`Password reset email sent to ${employer.email}`);
      } catch (error) {
        console.error('Error sending password reset:', error);
        if (error.code === 'auth/user-not-found') {
          toast.error('No user found with this email address');
        } else {
          toast.error('Error sending password reset email');
        }
      } finally {
        setIsResetting(null);
      }
    }
  };

  // Generate temporary password handler
  const handleGenerateTemporaryPassword = async (employer) => {
    if (!window.confirm(
      `Generate a new temporary password for ${employer.email}? Their current password will stop working immediately.`
    )) {
      return;
    }

    setIsGeneratingPassword(employer.id);
    try {
      const functions = getFunctions(app);
      const adminGenerateTemporaryPassword = httpsCallable(functions, 'adminGenerateTemporaryPassword');
      const result = await adminGenerateTemporaryPassword({ employerId: employer.id });
      setGeneratedPassword({ employer, password: result.data.temporaryPassword });
    } catch (error) {
      console.error('Error generating temporary password:', error);
      toast.error('Error generating temporary password');
    } finally {
      setIsGeneratingPassword(null);
    }
  };

  // Resend verification email handler
  const handleResendVerification = async (employer) => {
    if (!employer.email) {
      toast.error('No email address found for this employer');
      return;
    }

    if (employer.emailVerified) {
      toast.info('This email is already verified');
      return;
    }

    if (window.confirm(`Send verification email to ${employer.email}?`)) {
      setIsResendingVerification(employer.id);
      try {
        const functions = getFunctions(app);
        const resendVerificationEmail = httpsCallable(functions, 'resendVerificationEmail');

        await resendVerificationEmail({
          userId: employer.id,
          email: employer.email,
          userType: 'employer',
          userName: employer.fullName
        });

        toast.success(`Verification email sent to ${employer.email}`);
      } catch (error) {
        console.error('Error sending verification email:', error);
        if (error.code === 'functions/not-found') {
          toast.error('No user found with this email address in authentication');
        } else if (error.code === 'functions/permission-denied') {
          toast.error('Permission denied. Admin access required.');
        } else {
          toast.error(error.message || 'Error sending verification email');
        }
      } finally {
        setIsResendingVerification(null);
      }
    }
  };

  // Edit handlers
  const handleOpenEdit = (employer) => {
    setEditFormData({
      firstName: employer.firstName || '',
      lastName: employer.lastName || '',
      email: employer.email || '',
      phone: employer.phone || '',
      companyName: employer.companyName || employer.company || ''
    });
    setSelectedEmployer(employer);
    setIsEditModalOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedEmployer) return;

    setIsSaving(true);
    try {
      const updates = {
        firstName: editFormData.firstName,
        lastName: editFormData.lastName,
        email: editFormData.email,
        phone: editFormData.phone,
        companyName: editFormData.companyName,
        updatedAt: new Date().toISOString()
      };

      await update(ref(db, `employers/${selectedEmployer.id}`), updates);

      // Update local state
      setEmployers(employers.map(emp => {
        if (emp.id === selectedEmployer.id) {
          return {
            ...emp,
            ...updates,
            fullName: `${updates.firstName} ${updates.lastName}`
          };
        }
        return emp;
      }));

      toast.success('Employer updated successfully');
      setIsEditModalOpen(false);
      setSelectedEmployer(null);
    } catch (error) {
      console.error('Error updating employer:', error);
      toast.error('Error updating employer');
    } finally {
      setIsSaving(false);
    }
  };

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-950"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-blue-950 mb-4">All Employers</h2>

        {/* Search Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search by name, email, phone, company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            )}
          </div>
          <select
            value={packageFilter}
            onChange={(e) => setPackageFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            aria-label="Filter by package status"
          >
            <option value="all">All packages ({employers.length})</option>
            <option value="active">Active ({packageCounts.active})</option>
            <option value="expiring">Expiring in {EXPIRING_SOON_DAYS} days ({packageCounts.expiring})</option>
            <option value="expired">Expired ({packageCounts.expired})</option>
            <option value="none">No package ({packageCounts.none})</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            aria-label="Sort employers"
          >
            <option value="default">Default order</option>
            <option value="expiry">Package end date (soonest first)</option>
          </select>
        </div>

        {/* Results count and bulk actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
          <div className="text-sm text-gray-600">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredEmployers.length)} of {filteredEmployers.length} employers
            {searchTerm && ' (filtered)'}
          </div>
          {selectedIds.size > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
            >
              Delete Selected ({selectedIds.size})
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={el => {
                    if (el) el.indeterminate = isSomeSelected && !isAllSelected;
                  }}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Phone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Package
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedEmployers.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <FaSearch className="text-4xl text-gray-300 mb-2" />
                    <p>No employers found</p>
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              paginatedEmployers.map((employer) => (
                <tr key={employer.id} className={`hover:bg-gray-50 ${selectedIds.has(employer.id) ? 'bg-blue-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(employer.id)}
                      onChange={() => handleSelectOne(employer.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {employer.fullName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{employer.companyName || employer.company || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{employer.email}</div>
                    <div className="flex items-center gap-1 mt-1">
                      {employer.emailVerified ? (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <FaCheckCircle className="text-xs" /> Verified
                        </span>
                      ) : (
                        <span className="text-xs text-orange-600 flex items-center gap-1">
                          <FaEnvelope className="text-xs" /> Not Verified
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{employer.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const pkg = getPackageInfo(employer);
                      return (
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-900">{pkg.name || '—'}</span>
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${PACKAGE_PILL[pkg.state]}`}>
                              {pkg.label}
                            </span>
                          </div>
                          {pkg.endDate && (
                            <div className="text-xs text-gray-500 mt-1">
                              {pkg.state === 'expired' ? 'Ended' : 'Ends'} {formatDate(pkg.endDate)} · {describeDaysLeft(pkg)}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedEmployer(employer);
                          setIsModalOpen(true);
                        }}
                        className="text-blue-950 hover:text-[#cddd3a]"
                        title="View"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleOpenEdit(employer)}
                        className="text-green-600 hover:text-green-800"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleResetPassword(employer)}
                        disabled={isResetting === employer.id}
                        className="text-orange-600 hover:text-orange-800 disabled:opacity-50"
                        title="Reset Password"
                      >
                        {isResetting === employer.id ? (
                          <span className="animate-spin">...</span>
                        ) : (
                          <FaKey />
                        )}
                      </button>
                      <button
                        onClick={() => handleGenerateTemporaryPassword(employer)}
                        disabled={isGeneratingPassword === employer.id}
                        className="text-amber-600 hover:text-amber-800 disabled:opacity-50"
                        title="Generate Temporary Password"
                      >
                        {isGeneratingPassword === employer.id ? (
                          <span className="animate-spin">...</span>
                        ) : (
                          <FaLock />
                        )}
                      </button>
                      <button
                        onClick={() => handleResendVerification(employer)}
                        disabled={isResendingVerification === employer.id}
                        className={`disabled:opacity-50 ${
                          employer.emailVerified
                            ? 'text-green-600 cursor-default'
                            : 'text-purple-600 hover:text-purple-800'
                        }`}
                        title={employer.emailVerified ? 'Email Verified' : 'Resend Verification Email'}
                      >
                        {isResendingVerification === employer.id ? (
                          <span className="animate-spin">...</span>
                        ) : employer.emailVerified ? (
                          <FaCheckCircle />
                        ) : (
                          <FaEnvelope />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(employer.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {filteredEmployers.length > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Rows per page:</span>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="border rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                First
              </button>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                <FaChevronLeft />
              </button>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                <FaChevronRight />
              </button>
              <button
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {isModalOpen && selectedEmployer && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Employer Details
                </h3>
                <div className="border-t border-gray-200 py-3">
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Full Name</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedEmployer.fullName}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Company</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedEmployer.companyName || selectedEmployer.company || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Email</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedEmployer.email}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Phone</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedEmployer.phone}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">User Type</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedEmployer.userType || 'employer'}</dd>
                    </div>
                    {(() => {
                      const sub = selectedEmployer.subscription;
                      const pkg = getPackageInfo(selectedEmployer);
                      return (
                        <div className="border-t border-gray-200 pt-4">
                          <dt className="text-sm font-medium text-gray-500 mb-2">Package</dt>
                          {!sub ? (
                            <dd className="text-sm text-gray-900">No package on record</dd>
                          ) : (
                            <dd className="text-sm text-gray-900 space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{pkg.name || '—'}</span>
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${PACKAGE_PILL[pkg.state]}`}>
                                  {pkg.label}
                                </span>
                              </div>
                              <div>Started: {formatDate(sub.startDate)}</div>
                              <div>
                                {pkg.state === 'expired' ? 'Ended' : 'Ends'}: {formatDate(sub.endDate)}
                                {pkg.endDate && <span className="text-gray-500"> ({describeDaysLeft(pkg)})</span>}
                              </div>
                              {(sub.duration || sub.package?.duration) && (
                                <div>Duration: {sub.duration || sub.package?.duration} days</div>
                              )}
                              {sub.package?.jobPostLimit !== undefined && (
                                <div>Job post limit: {sub.package.jobPostLimit}</div>
                              )}
                              <div>
                                Payment: {sub.isFreeSubscription
                                  ? `Free${sub.obtainedVia ? ` (${sub.obtainedVia.replace(/_/g, ' ')})` : ''}`
                                  : (sub.paymentStatus || '—')}
                              </div>
                            </dd>
                          )}
                        </div>
                      );
                    })()}
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Created At</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date(selectedEmployer.createdAt).toLocaleDateString()}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-950 text-base font-medium text-white hover:bg-[#cddd3a] hover:text-blue-950 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    handleOpenEdit(selectedEmployer);
                  }}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm mt-2 sm:mt-0"
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && selectedEmployer && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSaveEdit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Edit Employer
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={editFormData.firstName}
                          onChange={handleEditChange}
                          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={editFormData.lastName}
                          onChange={handleEditChange}
                          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        value={editFormData.companyName}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={editFormData.email}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Note: Changing email here only updates the profile, not the login credentials.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={editFormData.phone}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-950 text-base font-medium text-white hover:bg-[#cddd3a] hover:text-blue-950 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedEmployer(null);
                    }}
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm mt-2 sm:mt-0"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {generatedPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-blue-950 mb-2">Temporary Password Generated</h3>
            <p className="text-gray-600 mb-4">
              This is now the active password for <strong>{generatedPassword.employer.email}</strong>.
              Share it with them directly — it will not be shown again.
            </p>
            <div className="flex items-center gap-2 mb-6">
              <code className="flex-1 bg-gray-100 border border-gray-300 rounded-md px-3 py-2 text-sm font-mono select-all">
                {generatedPassword.password}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(generatedPassword.password);
                  toast.success('Password copied to clipboard');
                }}
                className="p-2 text-blue-950 hover:text-[#cddd3a]"
                title="Copy to clipboard"
              >
                <FaCopy />
              </button>
            </div>
            <button
              onClick={() => setGeneratedPassword(null)}
              className="w-full px-4 py-2 bg-blue-950 text-white rounded-md hover:bg-blue-900 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllEmployers;
